'use strict';

/* globals wc jQuery Stripe yithStripePayments yithStripePaymentsElement */

import { useEffect, useRef } from '@wordpress/element';
import PaymentElementCheckout from './PaymentElementCheckout';
import { $ } from '../globals';
import AjaxHandler from './ajax';

/**
 * Class that init and manages all interactions with Stripe Element
 */
export default class PaymentElementBlock extends PaymentElementCheckout {
	/**
	 * Reference to node that works as elements wrapper
	 */
	nodeRef;

	/**
	 * Billing details
	 */
	billing;

	/**
	 * Constructor method
	 */
	constructor() {
		super();

		this.unmount = this.unmount.bind( this );
		this.onPaymentProcessing = this.onPaymentProcessing.bind( this );
	}

	/* === INITIALIZATION METHOD === */

	/**
	 * Init current object
	 */
	init() {
		this.registerMethod();
		this.initStripe();
		this.initHashChange();
	}

	/**
	 * Init Stripe object
	 */
	initStripe() {
		const { public_key: publicKey, account_id: acctId } =
			yithStripePaymentsElement;

		if ( ! publicKey || ! acctId ) {
			return;
		}

		this.stripe = Stripe( publicKey, {
			stripeAccount: acctId,
		} );
	}

	/* === BLOCK HANDLING === */

	getMethod() {
		const { slug, title } = yithStripePaymentsElement,
			Label = this.getLabel(),
			Content = this.getContent();

		return {
			name: slug,
			label: <Label />,
			content: <Content />,
			edit: <Content />,
			canMakePayment: () => true,
			ariaLabel: title,
			supports: {
				features: [
					'products',
					...( yithStripePaymentsElement.tokenization
						? [ 'tokenization' ]
						: [] ),
				],
			},
		};
	}

	getLabel() {
		return ( props ) => {
			const { PaymentMethodLabel } = props.components,
				{ title } = yithStripePaymentsElement;

			return <PaymentMethodLabel text={ title } />;
		};
	}

	getContent() {
		const slug = yithStripePaymentsElement.slug;

		return ( props ) => {
			const { billing, activePaymentMethod, eventRegistration } = props,
				{ onPaymentProcessing } = eventRegistration,
				{ description } = yithStripePaymentsElement;

			if ( slug !== activePaymentMethod ) {
				return;
			}

			// create reference for wrapper element.
			this.nodeRef = useRef();

			// registers current status of the billing details.
			this.billing = billing;

			// init elements when needed.
			useEffect( () => {
				this.initElements();
			} );

			// register payment processing observer.
			useEffect(
				() => onPaymentProcessing( this.onPaymentProcessing ),
				[ onPaymentProcessing ]
			);

			return (
				<>
					{ description ? <p>{ description }</p> : null }
					<div
						id={ this.target.replace( '#', '' ) }
						ref={ this.nodeRef }
					/>
				</>
			);
		};
	}

	registerMethod() {
		const { registerPaymentMethod } = wc.wcBlocksRegistry;

		registerPaymentMethod( this.getMethod() );
	}

	/* === CHECKOUT HANDLING === */

	/**
	 * Prints error messages relevant to customer
	 *
	 * @param {Object} error Error describing current error.
	 */
	onError( error ) {
		return {
			type: 'failure',
			message: error.message,
		};
	}

	/**
	 * During payment step processing, calls Stripe to generate payment method to use in API request.
	 */
	onPaymentProcessing() {
		return this.submitElements();
	}

	/* === ELEMENTS HANDLING === */

	/**
	 * Init Elements given that conditions are matched
	 *
	 * @return {Promise<void>}
	 */
	async initElements() {
		if ( ! this.stripe ) {
			return;
		}

		if ( ! this.elements ) {
			this.elements = this.stripe.elements(
				await this.getElementsDetails()
			);
			this.paymentElement = this.elements.create( 'payment', {
				layout: yithStripePaymentsElement.layout,
			} );
		}

		this.mount();
	}

	/**
	 * Submit Elements form and waits for response
	 */
	submitElements() {
		if ( ! this.stripe || ! this.elements ) {
			return;
		}

		return this.elements
			.submit()
			.then( this.afterElementsSubmit )
			.catch( this.onError );
	}

	/**
	 * Handles successful responses from Elements submit
	 */
	afterElementsSubmit() {
		return this.stripe
			.createPaymentMethod( {
				elements: this.elements,
				params: {
					billing_details: this.getBillingDetails(),
				},
			} )
			.then( ( { error, paymentMethod } ) => {
				if ( error ) {
					return this.onError( error );
				}

				return {
					type: 'success',
					meta: {
						paymentMethodData: {
							[ `yith_stripe_payments_${ yithStripePaymentsElement.slug }_payment_method` ]:
								paymentMethod.id,
						},
					},
				};
			} )
			.catch( this.onError );
	}

	/**
	 * Mount Elements on target node
	 */
	unmount() {
		this.paymentElement?.unmount();
	}

	/* === GETTERS METHOD === */

	/**
	 * Get an object with billing details about customer
	 *
	 * @return {Object} Billing object.
	 */
	getBillingDetails() {
		const address = this.billing.billingAddress,
			name = `${ address.first_name } ${ address.last_name }`;

		return {
			name,
			email: address?.email,
			phone: address?.phone,
			address: {
				country: address?.country,
				city: address?.city,
				line1: address?.address_1,
				line2: address?.address_2,
				postal_code: address?.postcode,
				state: address.state,
			},
		};
	}

	/**
	 * Returns jQuery node to the embed target
	 *
	 * @param {boolean} refresh Whether to refresh target or not
	 * @return {jQuery} jQuery node element.
	 */
	getTarget( refresh ) {
		if ( ! this.$target || refresh ) {
			this.$target = $( this.getTargetNode() );
		}

		return this.$target;
	}

	/**
	 * Returns DOM node to the embed target
	 *
	 * @return {Element} DOM node element.
	 */
	getTargetNode() {
		return this.nodeRef.current;
	}
}
